import { useEffect, useMemo, useState } from 'react'
import { Fingerprint, Nfc, ShieldAlert, ArrowDownAZ, Search, Plus, Users } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link, navigate } from '../router.js'
import { Trail } from '../navigation/Trail.js'
import { Select, useToast } from '../admin/primitives.js'
import { useApi, useAccessCaps, ActionButton, runCommand, AccessModal, SortHeader, tr, PERSON_STATUS, RESULT_LABELS, relativeDay, PersonAvatar } from './lib.js'
import { useMenuAcces } from './menu-acces.js'

/**
 * PERSONNES ACCESS — une vraie LISTE opérationnelle (§24-35) : recherche, tri,
 * filtres rapides, colonnes utiles (dernier passage, refus 7 j, identifiants,
 * liaison RH). L'état (recherche/tri/filtre) vit dans l'URL : SmartBack et le
 * bouton Retour restaurent tout (§33, §125). Ligne = fiche Personne (§34).
 */

interface Row {
  id: string; fullName: string; alias: string | null; email: string | null; status: string
  nfcCount: number; hasPin: boolean; hasTouchPass: boolean
  policies: { id: string; name: string }[]; groups: { id: string; name: string }[]
  personId: string | null; personName: string | null
  lastPassage: { at: string | null; door: string | null; result: string | null } | null
  lastGrantedAt: string | null
  deniedRecent: number
  photoUrl: string | null
}

const SORTS = [
  { value: 'name_asc', label: 'Nom A → Z' },
  { value: 'name_desc', label: 'Nom Z → A' },
  { value: 'last_access_desc', label: 'Dernier passage récent' },
  { value: 'last_access_asc', label: 'Dernier passage ancien' },
  { value: 'granted_desc', label: 'Dernier accès accordé' },
  { value: 'denied_desc', label: 'Refus récents' },
  { value: 'status', label: 'Statut' },
  { value: 'linked', label: 'Liaison RH' },
  { value: 'door_asc', label: 'Dernière porte' },
  { value: 'cred_desc', label: 'Identifiants' },
]
const FILTERS: { value: string; label: string }[] = [
  { value: 'all', label: 'Toutes' },
  { value: 'active', label: 'Actives' },
  { value: 'inactive', label: 'Désactivées' },
  { value: 'linked', label: 'Employés liés' },
  { value: 'unlinked', label: 'Non liées' },
  { value: 'with_credential', label: 'Avec identifiant' },
  { value: 'without_credential', label: 'Sans identifiant' },
  { value: 'denied_recent', label: 'Refus récents' },
]

export function AccessPeoplePage() {
  const meta = useShellMeta()
  const caps = useAccessCaps()
  const menu = useMenuAcces()
  const q0 = useMemo(() => new URLSearchParams(window.location.search), [])
  const [createOpen, setCreateOpen] = useState(q0.get('creer') === '1')
  const [search, setSearch] = useState(q0.get('q') ?? '')
  const [sort, setSort] = useState(q0.get('tri') ?? 'name_asc')
  const [filter, setFilter] = useState(q0.get('filtre') ?? 'all')
  const [group] = useState(q0.get('groupe') ?? '')
  const [debounced, setDebounced] = useState(search)
  useEffect(() => { const t = setTimeout(() => setDebounced(search), 250); return () => clearTimeout(t) }, [search])

  // L'URL reflète l'état — restaurée telle quelle au retour (§33).
  useEffect(() => {
    const p = new URLSearchParams()
    if (debounced) p.set('q', debounced)
    if (sort !== 'name_asc') p.set('tri', sort)
    if (filter !== 'all') p.set('filtre', filter)
    if (group) p.set('groupe', group)
    window.history.replaceState(null, '', `/securite/controle-acces/personnes${p.size ? `?${p}` : ''}`)
  }, [debounced, sort, filter, group])

  const api = new URLSearchParams()
  if (debounced) api.set('search', debounced)
  api.set('sort', sort); api.set('filter', filter)
  if (group) api.set('group', group)
  const { data } = useApi<{ people: Row[] }>(`/api/v1/access/people?${api}`)
  const groupName = group && data ? data.people.flatMap((p) => p.groups).find((g) => g.id === group)?.name ?? null : null

  return (
    <Shell active="security" meta={meta}>
      <main className="lc-page lc-stagger ac-page">
        <Trail />
        <header className="ac-head">
          <div><h1 className="lc-title">Personnes Access</h1>
            <p className="lc-subtitle">{data ? `${data.people.length} personne(s)` : '…'}{groupName ? ` · groupe ${groupName}` : ''} — utilisateurs UniFi Access, salariés ou non.</p>
          </div>
          <div className="ac-actions">
            <Link href="/securite/controle-acces/groupes" className="lc-btn lc-btn--ghost"><Users size={14} aria-hidden="true" /> Groupes</Link>
            <ActionButton caps={caps} action="CREATE_USER" label="Nouvelle personne" primary icon={<Plus size={14} aria-hidden="true" />} onRun={() => setCreateOpen(true)} />
          </div>
        </header>

        <div className="ac-toolbar">
          <label className="ac-searchwrap"><Search size={15} aria-hidden="true" />
            <input className="ac-search" placeholder="Nom, alias, e-mail, matricule, badge…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Rechercher une personne" />
          </label>
          <div className="ac-sortwrap"><ArrowDownAZ size={15} aria-hidden="true" />
            <Select value={sort} options={SORTS} onChange={setSort} ariaLabel="Trier les personnes" />
          </div>
        </div>
        <div className="ac-quickfilters" role="tablist" aria-label="Filtres rapides">
          {FILTERS.map((f) => (
            <button key={f.value} type="button" role="tab" aria-selected={filter === f.value} className={`ac-qf ${filter === f.value ? 'is-on' : ''}`} onClick={() => setFilter(f.value)}>{f.label}</button>
          ))}
        </div>

        <section className="lc-panel ac-plist-panel">
          <div className="lc-panel__body lc-panel__body--flush">
            {/* Entêtes TRIABLES (§45-47) : clic = ↑, re-clic = ↓. */}
            <div className="ac-plist-head ac-plist-head--v2">
              <SortHeader label="Personne" asc="name_asc" desc="name_desc" sort={sort} onSort={setSort} />
              <SortHeader label="Type" asc="type_asc" desc="type_desc" sort={sort} onSort={setSort} />
              <SortHeader label="Statut" asc="status" desc="status_desc" sort={sort} onSort={setSort} />
              <span className="ac-sorth">Règles</span>
              <SortHeader label="Identifiants" asc="cred_desc" desc="cred_asc" sort={sort} onSort={setSort} />
              <SortHeader label="Dernier passage" asc="last_access_desc" desc="last_access_asc" sort={sort} onSort={setSort} />
              <SortHeader label="Dernière porte" asc="door_asc" desc="door_desc" sort={sort} onSort={setSort} />
              <SortHeader label="Refus 7 j" asc="denied_desc" desc="denied_asc" sort={sort} onSort={setSort} />
            </div>
            <ul className="ac-plist">
              {(data?.people ?? []).map((p) => (
                <li key={p.id}>
                  <Link href={`/securite/controle-acces/personnes/${p.id}`} className="ac-prow" {...menu.personne({ id: p.id, nom: p.fullName || p.alias || null, personId: p.personId, personName: p.personName, email: p.email })}>
                    <span className="ac-prow__id">
                      <PersonAvatar name={p.fullName} photoUrl={p.photoUrl} />
                      <span className="ac-prow__name"><strong>{p.fullName || p.alias || '—'}</strong>{p.email ? <em>{p.email}</em> : null}</span>
                    </span>
                    <span className="ac-prow__type">{p.personId ? <span className="ac-typechip ac-typechip--linked"><Fingerprint size={11} aria-hidden="true" /> Employé lié</span> : <span className="ac-typechip">Personne Access</span>}</span>
                    <span><span className={`ac-status ac-status--${p.status.toLowerCase()}`}>{tr(PERSON_STATUS, p.status)}</span></span>
                    <span className="ac-prow__pol">{p.policies.length > 0 ? p.policies.map((x) => x.name).slice(0, 2).join(', ') + (p.policies.length > 2 ? ` +${p.policies.length - 2}` : '') : '—'}</span>
                    <span className="ac-prow__cred">
                      {p.nfcCount > 0 ? <span className="ac-credchip"><Nfc size={11} aria-hidden="true" /> {p.nfcCount}</span> : null}
                      {p.hasTouchPass ? <span className="ac-credchip">TP</span> : null}
                      {p.hasPin ? <span className="ac-credchip">PIN</span> : null}
                      {p.nfcCount === 0 && !p.hasTouchPass && !p.hasPin ? '—' : null}
                    </span>
                    <span className="ac-prow__last">
                      {p.lastPassage ? (<>
                        <strong>{relativeDay(p.lastPassage.at)}</strong>
                        <em>{p.lastPassage.result ? tr(RESULT_LABELS, p.lastPassage.result) : ''}</em>
                      </>) : <em>Jamais</em>}
                    </span>
                    <span className="ac-prow__pol">{p.lastPassage?.door ?? '—'}</span>
                    <span className="ac-prow__denied">{p.deniedRecent > 0 ? <span className="ac-deniedchip"><ShieldAlert size={11} aria-hidden="true" /> {p.deniedRecent}</span> : '—'}</span>
                  </Link>
                </li>
              ))}
            </ul>
            {data && data.people.length === 0 ? <p className="lc-hint" style={{ padding: 'var(--sp-4) var(--sp-6)' }}>Aucune personne ne correspond à ces critères.</p> : null}
          </div>
        </section>
        {createOpen ? <CreatePersonModal onClose={(createdId) => { setCreateOpen(false); if (createdId) navigate(`/securite/controle-acces/personnes/${createdId}`); else if (createdId === '') window.location.reload() }} /> : null}
      </main>
    </Shell>
  )
}

/** CRÉER UNE PERSONNE (§34, §104) — POST /users officiel, vraie UI. */
function CreatePersonModal({ onClose }: { onClose: (createdId: string | null) => void }) {
  const toast = useToast()
  const [v, setV] = useState({ first: '', last: '', email: '', phone: '', matricule: '' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const run = async () => {
    setBusy(true); setError(null)
    const user: Record<string, unknown> = { first_name: v.first, last_name: v.last }
    if (v.email) user.user_email = v.email
    if (v.phone) user.phone = v.phone
    if (v.matricule) user.employee_number = v.matricule
    const r = await runCommand('CREATE_USER', { user })
    setBusy(false)
    if (!r.ok) { setError(r.error); return }
    toast('ok', `${v.first} ${v.last} créé dans UniFi Access.`)
    const created = (r.result as { id?: string } | null)?.id ?? null
    await new Promise((res) => setTimeout(res, 1600))
    onClose(created ?? '')
  }
  return (
    <AccessModal title="Nouvelle personne Access" onClose={() => onClose(null)} footer={
      <><button type="button" className="lc-btn" onClick={() => onClose(null)}>Annuler</button>
        <button type="button" className="lc-btn lc-btn--primary" disabled={!v.first || !v.last || busy} onClick={() => void run()}>{busy ? 'Création…' : 'Créer dans UniFi Access'}</button></>
    }>
      <p className="lc-hint">Crée un utilisateur dans UniFi Access (pas un dossier RH — la liaison se fait ensuite depuis sa fiche).</p>
      <div className="ac-idform">
        <label className="ac-field">Prénom<input value={v.first} onChange={(e) => setV({ ...v, first: e.target.value })} /></label>
        <label className="ac-field">Nom<input value={v.last} onChange={(e) => setV({ ...v, last: e.target.value })} /></label>
        <label className="ac-field">E-mail (optionnel)<input value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} /></label>
        <label className="ac-field">Téléphone (optionnel)<input value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} /></label>
        <label className="ac-field">Matricule (optionnel)<input value={v.matricule} onChange={(e) => setV({ ...v, matricule: e.target.value })} /></label>
      </div>
      {error ? <p className="ac-err">{error}</p> : null}
    </AccessModal>
  )
}
